import type { DataResponseBody, PageResponse, FileResponse } from "./common.types";
import type { SiteRegion } from "./sites.types";

export type KnownEventCategory = "DETECTION" | "SAFETY";

export type EventCategory = KnownEventCategory | (string & {});

export const CCTV_EVENT_CATEGORIES: KnownEventCategory[] = ["DETECTION"];

export function isCctvEventCategory(category: EventCategory | undefined | null): boolean {
  if (category == null) return true;
  return (CCTV_EVENT_CATEGORIES as string[]).includes(category);
}

export type KnownEventType =
  | "NO_HELMET"
  | "FIRE"
  | "INTRUSION"
  | "GAS_THRESHOLD_EXCEEDED"
  | "BAND_VITAL_ABNORMAL"
  | "HEAT_INDEX_EXCEEDED";

export type EventType = KnownEventType | (string & {});

export type EventSeverity = "UNCERTAIN" | "ATTENTION" | "CAUTION" | "WARNING" | "CRITICAL";

export type EventSource = "GAS_SENSOR" | "SMART_BAND" | "THERMO_SENSOR" | "KMA";

export interface EventSite {
  id: number;
  name: string;
  region: SiteRegion;
}

export interface Event {
  id: number;
  eventId: string;
  timestamp: string;
  category: EventCategory;
  type: EventType;
  trackId: number;
  name: string;
  confidence: number;
  path: string;
  site: EventSite;
  snapshot?: FileResponse;
  video?: FileResponse;
  severity?: EventSeverity;
  source?: EventSource;
  deviceId?: string;
  bandId?: string;
  lat?: number;
  lon?: number;
  alt?: number;
  accuracyM?: number;
  payload?: Record<string, unknown>;
}

export type EventsResponse = DataResponseBody<PageResponse<Event>>;
export type EventDetailResponse = DataResponseBody<Event>;

export interface GetEventsParams {
  page?: number;
  size?: number;
  siteId?: number;
  query?: string;

  category?: EventCategory | EventCategory[];
}

export interface EventTypeInfo {
  name: EventType;
  displayName: string;
}

export interface EventCategoryInfo {
  name: EventCategory;
  displayName: string;
  types: EventTypeInfo[];
}

export type EventCategoriesResponse = DataResponseBody<EventCategoryInfo[]>;

export type EventSeverityBucket = "critical" | "warning" | "caution" | "attention" | "uncertain";

const SEVERITY_BUCKET: Record<EventSeverity, EventSeverityBucket> = {
  CRITICAL: "critical",
  WARNING: "warning",
  CAUTION: "caution",
  ATTENTION: "attention",
  UNCERTAIN: "uncertain",
};

const SEVERITY_FALLBACK: EventSeverityBucket = "attention";

export function eventSeverity(event: Pick<Event, "severity">): EventSeverityBucket {
  if (!event.severity) return SEVERITY_FALLBACK;
  return SEVERITY_BUCKET[event.severity] ?? SEVERITY_FALLBACK;
}

export function isUncertainEvent(event: Pick<Event, "severity">): boolean {
  return eventSeverity(event) === "uncertain";
}

export const EVENT_SEVERITY_STYLES = {
  critical: { bg: "bg-red-200", text: "text-red-800", label: "위험" },
  warning: { bg: "bg-orange-200", text: "text-orange-800", label: "경고" },
  caution: { bg: "bg-yellow-200", text: "text-yellow-800", label: "주의" },
  attention: { bg: "bg-blue-200", text: "text-blue-800", label: "관심" },
  uncertain: { bg: "bg-slate-200", text: "text-slate-700", label: "불확실" },
} as const;

export function severityChip(severity: EventSeverity | null | undefined) {
  return EVENT_SEVERITY_STYLES[severity ? SEVERITY_BUCKET[severity] : SEVERITY_FALLBACK];
}

export const EVENT_REGION_MAP: Record<SiteRegion, string> = {
  SEOUL: "서울",
  GYEONGGI_INCHEON: "경기",
  GANGWON: "강원",
  CHUNGCHEONG: "충청",
  JEOLLA: "전라",
  GYEONGSANG: "경상",
  JEJU: "제주",
};

export const EVENT_REGIONS = ["전체", ...Object.values(EVENT_REGION_MAP)] as const;
export type EventRegionTab = (typeof EVENT_REGIONS)[number];

export const EVENT_REGION_LABEL_TO_KEY: Record<string, SiteRegion> = Object.fromEntries(
  Object.entries(EVENT_REGION_MAP).map(([key, label]) => [label, key as SiteRegion])
);

export type StompEventType = EventType;
export type StompEventCategory = EventCategory;

export interface StompEventResponse {
  eventId: string;
  id: number;
  name: string;
  type: StompEventType;
  category: StompEventCategory;
  confidence: number;
  timestamp: string;
  trackId: number;
  path: string;
  snapshot?: FileResponse;
  video?: FileResponse;
  severity?: EventSeverity;
}

export interface SiteEmergencyPayload {
  workerId: string;
  position: { lng: number; lat: number; altitude: number };
  vitals: { temperature: number; heartRate: number };
}

export interface SiteEvent extends Event {
  emergency?: SiteEmergencyPayload;
}
